/**
 * Globální vyhledávání (spec 08 §3).
 *
 * Hledá ve znalostní bázi — vlastní insighty i poznámky z HumanAgentWiki
 * (`/knowledge/search` obojí vrací v jedné odpovědi). Vyhledávání napříč
 * experimenty a placementy backend zatím jako jeden endpoint nemá; do té doby
 * se na ně chodí přes jejich vlastní stránky.
 */
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useKnowledgeSearch } from '@/api/queries';

export function GlobalSearch() {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();
  const { data, isFetching } = useKnowledgeSearch(query);

  // Ctrl/Cmd+K otevře hledání odkudkoli.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        inputRef.current?.focus();
        setOpen(true);
      }
      if (e.key === 'Escape') setOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const go = (to: string) => {
    setOpen(false);
    setQuery('');
    navigate(to);
  };

  const insights = data?.insights ?? [];
  const wikiNotes = data?.wiki_notes ?? [];
  const hasResults = insights.length > 0 || wikiNotes.length > 0;

  return (
    <div className="global-search">
      <svg
        className="global-search__icon"
        viewBox="0 0 16 16"
        width="14"
        height="14"
        fill="none"
        aria-hidden="true"
      >
        <circle cx="7" cy="7" r="4.5" stroke="currentColor" strokeWidth="1.3" />
        <path d="M10.5 10.5 14 14" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
      </svg>
      <input
        ref={inputRef}
        type="search"
        className="global-search__input"
        placeholder="Hledat ve znalostech…  (Ctrl+K)"
        value={query}
        onChange={(e) => {
          setQuery(e.target.value);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        aria-label="Globální vyhledávání"
      />

      {open && query.trim().length > 1 ? (
        <>
          <div className="popover-backdrop" onClick={() => setOpen(false)} aria-hidden="true" />
          <div className="global-search__results" role="listbox">
            {isFetching ? <div className="global-search__status">Hledám…</div> : null}

            {!isFetching && !hasResults ? (
              <div className="global-search__status">
                Nic nenalezeno. Zkus jiná slova — vyhledávání je sémantické, takže funguje
                i na opis („zavíratko“, „proč klesá refresh“).
              </div>
            ) : null}

            {data?.wiki_error ? (
              <div className="global-search__status">
                Wiki se nepodařilo dotázat ({data.wiki_error}) — výsledky jsou jen
                z vlastních poznatků.
              </div>
            ) : null}

            {insights.length > 0 ? (
              <section>
                <h4>Poznatky</h4>
                {insights.slice(0, 6).map((insight) => (
                  <button
                    key={insight.slug}
                    type="button"
                    className="global-search__hit"
                    onClick={() => go(`/knowledge?note=${encodeURIComponent(insight.slug)}`)}
                  >
                    <strong>{insight.title}</strong>
                    <span className="global-search__snippet">{insight.summary}</span>
                  </button>
                ))}
              </section>
            ) : null}

            {wikiNotes.length > 0 ? (
              <section>
                <h4>Wiki</h4>
                {wikiNotes.slice(0, 5).map((note) => (
                  <a
                    key={note.slug}
                    className="global-search__hit"
                    href={note.url ?? '#'}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <strong>{note.title}</strong>
                    {/* Wiki vrací celý markdown — do našeptávače stačí začátek. */}
                    <span className="global-search__snippet">
                      {note.body_md.replace(/[#*`>_-]/g, ' ').replace(/\s+/g, ' ').slice(0, 140)}
                    </span>
                  </a>
                ))}
              </section>
            ) : null}
          </div>
        </>
      ) : null}
    </div>
  );
}
